import React, { useEffect, useState } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchStaffsMonthAttendanceList,
  fetchStaffAttendanceYearlyStats,
} from "../../../../Store/Slices/Admin/staffAttendance/staffAttendance.thunk";
import Spinner from "../../../../Components/Common/Spinner";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaDoorOpen,
  FaClock,
} from "react-icons/fa";
import { MdAccessTime } from "react-icons/md";
import { useParams } from "react-router-dom";
import CalendarHeader from "./CalendarHeader";

const Attendance = () => {
  const { sid } = useParams();
  const dispatch = useDispatch();
  const { monthlyAttendance, yearlyStats, loading } = useSelector(
    (state) => state.admin.staffAttendance
  );

  const [range, setRange] = useState({
    fromDate: moment().startOf("year").format("YYYY-MM-DD"),
    toDate: moment().endOf("year").format("YYYY-MM-DD"),
  });

  const [selectedMonth, setSelectedMonth] = useState(moment().month());
  const [selectedYear, setSelectedYear] = useState(moment().year());

  const currentYear = moment().year();
  const yearList = [currentYear - 1, currentYear, currentYear + 1];

  const onMonthChange = (newMonth) => setSelectedMonth(newMonth);
  const onYearChange = (newYear) => setSelectedYear(newYear);

  useEffect(() => {
    dispatch(
      fetchStaffsMonthAttendanceList({
        staffId: sid,
        month: selectedMonth + 1,
        year: selectedYear,
      })
    );
  }, [dispatch, selectedMonth, selectedYear, sid]);

  useEffect(() => {
    dispatch(
      fetchStaffAttendanceYearlyStats({
        fromDate: range.fromDate,
        toDate: range.toDate,
        staffId: sid,
      })
    );
  }, [dispatch, range, sid]);

  const staffAttendance = Array.isArray(monthlyAttendance)
    ? monthlyAttendance.find((s) => s.staffId === sid)
    : monthlyAttendance?.staffId === sid
    ? monthlyAttendance
    : null;

  // === Monthly Totals ===
  const totalDaysSoFar =
    selectedYear === moment().year() && selectedMonth === moment().month()
      ? moment().date()
      : moment(`${selectedYear}-${selectedMonth + 1}`, "YYYY-MM").daysInMonth();

  const present =
    staffAttendance?.attendanceStatus?.filter((d) => d.status === "present")
      .length || 0;
  const absent =
    staffAttendance?.attendanceStatus?.filter((d) => d.status === "absent")
      .length || 0;
  const leave =
    staffAttendance?.attendanceStatus?.filter((d) => d.status === "leave")
      .length || 0;
  const tardy = Math.max(totalDaysSoFar - (present + absent + leave), 0);

  const getPercentage = (count) =>
    totalDaysSoFar > 0 ? ((count / totalDaysSoFar) * 100).toFixed(1) : "0.0";


  const totalHours =
    staffAttendance?.attendanceStatus?.reduce((acc, d) => {
      if (d.startTime && d.endTime) {
        const start = moment(d.startTime);
        const end = moment(d.endTime);
        if (end.isAfter(start)) {
          acc += end.diff(start, "hours", true);
        }
      }
      return acc;
    }, 0) || 0;

  const formattedHours = Math.floor(totalHours);
  const formattedMinutes = Math.round((totalHours - formattedHours) * 60);

  if (loading) {
    return (
      <div className="flex w-full h-[90vh] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6 p-6 bg-gray-50 min-h-screen">
      {/* Left Sidebar */}
      {staffAttendance && (
        <div className="w-full lg:w-[30%] flex flex-col gap-6">
          <div className="flex flex-col gap-6 h-full">
            {/* Profile Card */}
            <div className="bg-white rounded-xl shadow-lg p-3 flex flex-col items-center gap-4">
              <img
                src={staffAttendance?.profile}
                alt={staffAttendance?.name}
                className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
              />
              <div className="flex flex-col items-center gap-0.5">
                <p className="m-0 font-semibold  md:text-xl text-gray-800">
                  {staffAttendance?.name}
                </p>
                <p className="m-0 text-sm text-gray-500 capitalize">
                  {staffAttendance?.role}
                </p>
                <p className="m-0 text-sm text-gray-400">
                  {staffAttendance?.email}
                </p>
              </div>
            </div>

            {/* Monthly Hours Card */}
            <div className="bg-gradient-to-r from-indigo-500 to-blue-600 text-white rounded-xl shadow-lg p-3 flex flex-col items-center justify-center gap-3">
              <FaClock className="text-4xl" />
              <h3 className="text-lg font-semibold">This Month’s Hours</h3>
              <p className="text-2xl font-bold">
                {formattedHours}h {formattedMinutes}m
              </p>
              <p className="text-xs opacity-80">
                {totalDaysSoFar} days considered
              </p>
            </div>

            {/* Yearly Stats Card */}
            {yearlyStats && (
              <div className="w-full bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl shadow-lg p-4 flex flex-col gap-3">
                <h3 className="text-base font-semibold">Yearly Stats</h3>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-medium text-white">
                    Select Range
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="date"
                      className="px-2 py-1 rounded-md text-xs text-black w-full sm:w-1/2"
                      value={range.fromDate}
                      onChange={(e) =>
                        setRange({ ...range, fromDate: e.target.value })
                      }
                    />
                    <input
                      type="date"
                      className="px-2 py-1 rounded-md text-xs text-black w-full sm:w-1/2"
                      value={range.toDate}
                      onChange={(e) =>
                        setRange({ ...range, toDate: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                  <div className="bg-white text-green-600 rounded-md p-2 shadow text-center">
                    <p className="text-xs">Present</p>
                    <p className="text-base font-bold">{yearlyStats.present}</p>
                  </div>
                  <div className="bg-white text-red-600 rounded-md p-2 shadow text-center">
                    <p className="text-xs">Absent</p>
                    <p className="text-base font-bold">{yearlyStats.absent}</p>
                  </div>
                  <div className="bg-white text-purple-600 rounded-md p-2 shadow text-center">
                    <p className="text-xs">Leave</p>
                    <p className="text-base font-bold">{yearlyStats.leave}</p>
                  </div>
                  <div className="bg-white text-blue-600 rounded-md p-2 shadow text-center">
                    <p className="text-xs">Tardy</p>
                    <p className="text-base font-bold">{yearlyStats.tardy}</p>
                  </div>
                  <div className="col-span-1 sm:col-span-2 bg-white text-indigo-600 rounded-md p-2 shadow flex flex-col sm:flex-row justify-between items-center">
                    <p className="text-xs">Total Logged Hours</p>
                    <p className="text-base font-bold">
                      {Math.floor(yearlyStats.totalHours)}h{" "}
                      {Math.round((yearlyStats.totalHours % 1) * 60)}m
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Right Content*/}
      <div className="w-full lg:w-[70%] flex flex-col gap-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {/* Present */}
          <div className="flex items-center bg-green-100 p-4 rounded-lg shadow-sm">
            <div className="bg-white rounded-full p-2">
              <FaCheckCircle className="text-3xl text-green-500" />
            </div>
            <div className="flex flex-col items-start ml-4">
              <span className="text-3xl text-gray-700">
                {present}{" "}
                <span className="text-sm">({getPercentage(present)}%)</span>
              </span>
              <span className="mt-1 text-green-600 font-medium">
                Total Present
              </span>
            </div>
          </div>
          {/* Absent */}
          <div className="flex items-center bg-red-100 p-4 rounded-lg shadow-sm">
            <div className="bg-white rounded-full p-2">
              <FaTimesCircle className="text-3xl text-red-500" />
            </div>
            <div className="flex flex-col items-start ml-4">
              <span className="text-3xl text-gray-700">
                {absent}{" "}
                <span className="text-sm">({getPercentage(absent)}%)</span>
              </span>
              <span className="mt-1 text-red-600 font-medium">
                Total Absent
              </span>
            </div>
          </div>
          {/* Leave */}
          <div className="flex items-center bg-purple-100 p-4 rounded-lg shadow-sm">
            <div className="bg-white rounded-full p-2">
              <FaDoorOpen className="text-3xl text-purple-500" />
            </div>
            <div className="flex flex-col items-start ml-4">
              <span className="text-3xl text-gray-700">
                {leave}{" "}
                <span className="text-sm">({getPercentage(leave)}%)</span>
              </span>
              <span className="mt-1 text-purple-600 font-medium">
                Total Leave
              </span>
            </div>
          </div>
          {/* Tardy */}
          <div className="flex items-center bg-blue-100 p-4 rounded-lg shadow-sm">
            <div className="bg-white rounded-full p-2">
              <MdAccessTime className="text-3xl text-blue-500" />
            </div>
            <div className="flex flex-col items-start ml-4">
              <span className="text-3xl text-gray-700">
                {tardy}{" "}
                <span className="text-sm">({getPercentage(tardy)}%)</span>
              </span>
              <span className="mt-1 text-blue-600 font-medium">
                Total Tardy
              </span>
            </div>
          </div>
        </div>

        {/* Calendar */}
        <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100 flex-1">
          <CalendarHeader
            attendanceData={(staffAttendance?.attendanceStatus || [])?.map(
              (d) => ({
                date: moment.utc(d.date).format("YYYY-MM-DD"),
                status: d.status === "not marked" ? " " : d.status,
              })
            )}
            onMonthChange={onMonthChange}
            onYearChange={onYearChange}
            yearList={yearList}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
          />
        </div>
      </div>
    </div>
  );
};

export default Attendance;
